// Energy Impact & ROI Calculator — math.
//
// Three-option comparison:
//   A. Do Nothing                       — existing luminaires, existing runtime
//   B. Lighting Upgrade Only            — proposed LEDs at the "basic upgrade" runtime
//   C. Lighting Upgrade + Helvar        — proposed LEDs @ 100% runtime, then Helvar
//                                         control upgrades applied sequentially
//
// Key insight: The Helvar payback is driven by the B → C savings (the uplift that
// controls add over and above a plain lighting upgrade), because the controls
// premium CapEx is the extra money spent to get Helvar INSTEAD of the basic
// competitor controls that would have come with option B.

// Currency list: EUR / GBP / USD pinned at the top for Helvar's primary markets,
// then all other currencies in alphabetical order by ISO code. Symbols chosen
// to render cleanly in jsPDF's default Helvetica (WinAnsi) font; codes are
// used as symbol fallbacks where a native glyph would not render.
export const CURRENCIES = {
  EUR: { code: "EUR", symbol: "€" },
  GBP: { code: "GBP", symbol: "£" },
  USD: { code: "USD", symbol: "$" },
  AED: { code: "AED", symbol: "AED" },
  AUD: { code: "AUD", symbol: "A$" },
  BRL: { code: "BRL", symbol: "R$" },
  CAD: { code: "CAD", symbol: "C$" },
  CHF: { code: "CHF", symbol: "CHF" },
  CNY: { code: "CNY", symbol: "¥" },
  CZK: { code: "CZK", symbol: "Kč" },
  DKK: { code: "DKK", symbol: "kr" },
  HKD: { code: "HKD", symbol: "HK$" },
  HUF: { code: "HUF", symbol: "Ft" },
  INR: { code: "INR", symbol: "Rs" },
  JPY: { code: "JPY", symbol: "¥" },
  KRW: { code: "KRW", symbol: "KRW" },
  KWD: { code: "KWD", symbol: "KD" },
  MXN: { code: "MXN", symbol: "Mex$" },
  NOK: { code: "NOK", symbol: "kr" },
  NZD: { code: "NZD", symbol: "NZ$" },
  PLN: { code: "PLN", symbol: "zł" },
  QAR: { code: "QAR", symbol: "QR" },
  RON: { code: "RON", symbol: "lei" },
  SAR: { code: "SAR", symbol: "SAR" },
  SEK: { code: "SEK", symbol: "kr" },
  SGD: { code: "SGD", symbol: "S$" },
  TRY: { code: "TRY", symbol: "TL" },
  ZAR: { code: "ZAR", symbol: "R" },
};

export const UPGRADE_DEFINITIONS = [
  { id: "presence", defaultPct: 20, hasCoverage: true, defaultCoveragePct: 100 },
  { id: "absence", defaultPct: 15, hasCoverage: true, defaultCoveragePct: 100 },
  { id: "daylight", defaultPct: 20, hasCoverage: true, defaultCoveragePct: 100 },
  { id: "scheduling", defaultPct: 5, hasCoverage: true, defaultCoveragePct: 100 },
  { id: "pirTimeout", defaultPct: 10, insights: true },
  { id: "spaceOptimisation", defaultPct: 10, insights: true },
];

export function defaultUpgradesState() {
  return UPGRADE_DEFINITIONS.reduce((acc, u) => {
    acc[u.id] = {
      enabled: true,
      savingsPct: u.defaultPct,
      ...(u.hasCoverage ? { coveragePct: u.defaultCoveragePct ?? 100 } : {}),
    };
    return acc;
  }, {});
}

// ---------------------------------------------------------------------------
// Option kWh calculators
// ---------------------------------------------------------------------------

// A — For RENOVATION: keep the EXISTING luminaires at their current runtime
//     (Do Nothing). Uses `existingLuminaires` × `existingWattage`.
//     For NEW BUILD: LED-only baseline with NO controls, i.e. NEW luminaires
//     at proposed wattage × 100% runtime (lights always on).
export function optA_doNothingKwh({
  luminaires,
  existingLuminaires,
  burningHoursPerWeek,
  existingWattage,
  existingRuntimePct,
  proposedWattage,
  projectType,
}) {
  const hours = burningHoursPerWeek * 52;
  if (projectType === "newBuild") {
    // New build: no existing fixtures. Baseline = new LED count at 100% runtime.
    return (luminaires * proposedWattage * hours) / 1000;
  }
  const runtime = Math.max(0, Math.min(100, existingRuntimePct || 0)) / 100;
  // Renovation: use existing fixture count × existing wattage.
  const qty = existingLuminaires ?? luminaires;
  return ((qty * existingWattage * hours) / 1000) * runtime;
}

// B — Lighting Upgrade Only: new LEDs at the "basic upgrade" runtime (e.g. basic PIR).
export function optB_upgradeOnlyKwh({
  luminaires,
  burningHoursPerWeek,
  proposedWattage,
  upgradeRuntimePct,
}) {
  const hours = burningHoursPerWeek * 52;
  const runtime = Math.max(0, Math.min(100, upgradeRuntimePct || 0)) / 100;
  return ((luminaires * proposedWattage * hours) / 1000) * runtime;
}

// C — Lighting Upgrade + Helvar Controls: proposed LEDs starting from 100% runtime,
// then each enabled upgrade reduces the remaining load in order.
//
// Strategies with `hasCoverage` (e.g. Daylight Harvesting) scale their savings
// pct by coverage / 100, so a 20% saving applied to only 50% of luminaires
// becomes a 10% effective reduction on the remaining load.
export function optC_helvarKwh({
  luminaires,
  burningHoursPerWeek,
  proposedWattage,
  upgrades,
}) {
  const hours = burningHoursPerWeek * 52;
  let kwh = (luminaires * proposedWattage * hours) / 1000; // 100% runtime
  for (const def of UPGRADE_DEFINITIONS) {
    const u = upgrades?.[def.id];
    if (!u || !u.enabled) continue;
    const pct = Math.max(0, Math.min(100, Number(u.savingsPct) || 0));
    const coverage = def.hasCoverage
      ? Math.max(0, Math.min(100, Number(u.coveragePct ?? def.defaultCoveragePct ?? 100) || 0))
      : 100;
    const effectivePct = (pct * coverage) / 100;
    kwh = kwh * (1 - effectivePct / 100);
  }
  return kwh;
}

export const DEFAULT_CO2_PER_KWH = 0.23;

export function co2TonnesFromAnnualKwh(annualKwh, years, co2PerKwh) {
  return (annualKwh * years * co2PerKwh) / 1000;
}

export function totalProjectCapex({
  installationCost,
  luminaireCost,
  helvarSystemCost,
}) {
  return (
    (installationCost || 0) + (luminaireCost || 0) + (helvarSystemCost || 0)
  );
}

export function controlsPremiumCapex({ helvarSystemCost, competitorControlsCost }) {
  return (helvarSystemCost || 0) - (competitorControlsCost || 0);
}

export function paybackYears(premium, annualSavings) {
  if (premium <= 0) return 0;
  if (annualSavings <= 0) return null;
  return premium / annualSavings;
}

// ---------------------------------------------------------------------------
// Master reactive calculator
// ---------------------------------------------------------------------------

export function calculateAll(state) {
  const {
    luminaires,
    existingLuminaires,
    burningHoursPerWeek,
    existingWattage,
    proposedWattage,
    existingRuntimePct,
    upgradeRuntimePct,
    pricePerKwh,
    installationCost,
    luminaireCost,
    competitorControlsCost,
    helvarSystemCost,
    upgrades,
    projectType,
  } = state;

  const kwhA = optA_doNothingKwh({
    luminaires,
    existingLuminaires,
    burningHoursPerWeek,
    existingWattage,
    existingRuntimePct,
    proposedWattage,
    projectType,
  });
  const kwhB = optB_upgradeOnlyKwh({
    luminaires,
    burningHoursPerWeek,
    proposedWattage,
    upgradeRuntimePct,
  });
  const kwhC = optC_helvarKwh({
    luminaires,
    burningHoursPerWeek,
    proposedWattage,
    upgrades,
  });

  const price = pricePerKwh || 0;

  // Annual financial costs per option
  const costA = kwhA * price;
  const costB = kwhB * price;
  const costC = kwhC * price;

  // Savings deltas
  const savedA_to_C = Math.max(0, kwhA - kwhC); // total vs do-nothing
  const savedA_to_B = Math.max(0, kwhA - kwhB); // upgrade-only savings
  const savedB_to_C = Math.max(0, kwhB - kwhC); // Helvar uplift (KEY metric)

  const financialA_to_C = savedA_to_C * price;
  const financialA_to_B = savedA_to_B * price;
  const financialB_to_C = savedB_to_C * price; // drives Helvar payback

  const co2PerKwh = Math.max(0, Number(state.co2PerKwh) || 0);
  const co2A_to_C = co2TonnesFromAnnualKwh(savedA_to_C, 10, co2PerKwh);
  const co2A_to_B = co2TonnesFromAnnualKwh(savedA_to_B, 10, co2PerKwh);
  const co2B_to_C = co2TonnesFromAnnualKwh(savedB_to_C, 10, co2PerKwh); // Helvar uplift

  const totalCapex = totalProjectCapex({
    installationCost,
    luminaireCost,
    helvarSystemCost,
  });
  // Controls premium — driver of payback. When the user opts to compare
  // against a competitor system, we use the delta (Helvar − competitor).
  // Otherwise we fall back to the full Helvar cost, which yields a
  // WORST-CASE payback the sales rep can honestly quote.
  const compareCompetitor = !!state.compareCompetitor;
  const premium = compareCompetitor
    ? (helvarSystemCost || 0) - (competitorControlsCost || 0)
    : (helvarSystemCost || 0);

  // Payback on the Helvar controls premium, driven by the B → C uplift.
  const payback = paybackYears(premium, financialB_to_C);

  return {
    // Option kWh
    kwhA,
    kwhB,
    kwhC,
    // Annual costs per option
    costA,
    costB,
    costC,
    // Savings
    savedA_to_C,
    savedA_to_B,
    savedB_to_C,
    financialA_to_C,
    financialA_to_B,
    financialB_to_C,
    co2A_to_C,
    co2A_to_B,
    co2B_to_C,
    co2PerKwh,
    // CapEx
    installationCost: installationCost || 0,
    luminaireCost: luminaireCost || 0,
    competitorControlsCost: competitorControlsCost || 0,
    helvarSystemCost: helvarSystemCost || 0,
    totalProjectCapex: totalCapex,
    controlsPremiumCapex: premium,
    compareCompetitor,
    pricePerKwh: price,
    payback,
  };
}

export function formatCurrency(value, symbol = "€") {
  if (!Number.isFinite(value)) return `${symbol}0`;
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const formatted = abs.toLocaleString("en-GB", { maximumFractionDigits: 0 });
  return `${sign}${symbol}${formatted}`;
}

export function formatCompact(value, symbol = "€") {
  if (!Number.isFinite(value)) return `${symbol}0`;
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${symbol}${(abs / 1_000).toFixed(1)}k`;
  return `${sign}${symbol}${abs.toFixed(0)}`;
}

export function formatKwh(value) {
  if (!Number.isFinite(value)) return "0";
  return Math.round(value).toLocaleString("en-GB");
}

// ---------------------------------------------------------------------------
// Scenarios: single source of truth for what's shown in the comparison table,
// bar chart, and PDF — with correct labels + row count per project type.
//
// Renovation → 3 rows [A / B / C]
// New Build  → 2 rows [A / B]  (no "do nothing" — new builds must install)
//
// Both cases still compute the Helvar uplift as (basic-controls scenario →
// helvar scenario), so payback and environmental impact metrics are unchanged.
// ---------------------------------------------------------------------------

export function getScenarios(results, state) {
  const isNewBuild = state?.projectType === "newBuild";

  if (isNewBuild) {
    // 2-option comparison for new builds. Baseline = Option A (basic controls).
    const kwhBase = results.kwhB;
    return {
      isNewBuild: true,
      baselineLabelKey: "scenario.new_a_led_basic",
      vsColumnHeaderKey: "results.vs_basic_controls",
      rows: [
        {
          letter: "A",
          nameKey: "scenario.new_a_led_basic",
          kwh: results.kwhB,
          cost: results.costB,
          co2: 0,
          reduction: null,
          recommended: false,
        },
        {
          letter: "B",
          nameKey: "scenario.new_b_led_helvar",
          kwh: results.kwhC,
          cost: results.costC,
          co2: results.co2B_to_C,
          reduction:
            kwhBase > 0 ? ((kwhBase - results.kwhC) / kwhBase) * 100 : null,
          recommended: true,
        },
      ],
    };
  }

  // Renovation — 3 rows, existing luminaires as the baseline.
  const kwhBase = results.kwhA;
  return {
    isNewBuild: false,
    baselineLabelKey: "scenario.a_do_nothing",
    vsColumnHeaderKey: "results.vs_do_nothing",
    rows: [
      {
        letter: "A",
        nameKey: "scenario.a_do_nothing",
        kwh: results.kwhA,
        cost: results.costA,
        co2: 0,
        reduction: null,
        recommended: false,
      },
      {
        letter: "B",
        nameKey: "scenario.b_led",
        kwh: results.kwhB,
        cost: results.costB,
        co2: results.co2A_to_B,
        reduction:
          kwhBase > 0 ? ((kwhBase - results.kwhB) / kwhBase) * 100 : null,
        recommended: false,
      },
      {
        letter: "C",
        nameKey: "scenario.c_led_helvar",
        kwh: results.kwhC,
        cost: results.costC,
        co2: results.co2A_to_C,
        reduction:
          kwhBase > 0 ? ((kwhBase - results.kwhC) / kwhBase) * 100 : null,
        recommended: true,
      },
    ],
  };
}
